import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { isVideoCapableModel } from '@inference.engine/model-registry/web';
import { useCurrentAgent } from '@/features/chats/stores';
import { CHAT_VIDEO_ATTACHMENTS_FLAG } from './file-upload.constants';

/**
 * Whether this chat may take a video, which is a question about the models rather than the agent:
 * an agent does not read video, a model does. Asked of the same registry table the backend gates
 * on, so the control disappears for exactly the chats the backend would refuse.
 *
 * Asked of every model the agent has enabled rather than the one currently picked, because
 * `useModelCompatibilityGuard` switches an incompatible selection onto a video-capable one the
 * moment a video is attached. Gating on the current pick would hide the control before that switch
 * could happen.
 */
export const useVideoAttachmentsEnabled = (): boolean => {
  const flagEnabled = useBooleanFlagValue(CHAT_VIDEO_ATTACHMENTS_FLAG, false);
  const currentAgent = useCurrentAgent();

  return flagEnabled && hasVideoCapableModel(currentAgent?.enabledModels);
};

/**
 * Whether any model this agent has enabled can read a video. Shared with the composer, which asks
 * the same question of the agent it is displaying to explain a blocked send: two copies would let
 * the attach control and the send button disagree about the same chat.
 */
export const hasVideoCapableModel = (enabledModels: readonly string[] | undefined): boolean =>
  enabledModels?.some((model) => isVideoCapableModel(model)) ?? false;
